import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { CustomOrderEntry } from '@cart/const';
import { CustomActiveCartService } from '@cart/data-access';
import { DeviceService } from '@shared/services';
import { ScrollToService } from '@shared/v2';
import { distinctUntilChanged, filter, take } from 'rxjs';
import { CartListItemComponent } from './cart-list-item';

@Component({
  selector: 'sd-cart-list',
  imports: [CartListItemComponent],
  templateUrl: './cart-list.component.html',
  styleUrls: ['./cart-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartListComponent implements OnDestroy {
  private destroyRef = inject(DestroyRef);
  private deviceService = inject(DeviceService);
  private activeCartService = inject(CustomActiveCartService);
  private scrollToService = inject(ScrollToService);

  readonly entries = input.required<CustomOrderEntry[]>();

  private form = new FormGroup({});
  private formVersion = signal(0);

  private isStable = toSignal(this.activeCartService.isStable(), { initialValue: false });
  private isInternalLoading = computed(() => !this.isStable());

  protected readonly sortedEntries = computed(() => {
    const items = this.entries();

    return [...items].sort((a, b) => {
      const aIsGift = a.basePrice?.value === 0;
      const bIsGift = b.basePrice?.value === 0;

      return aIsGift === bIsGift ? 0 : aIsGift ? -1 : 1;
    });
  });

  protected readonly viewModel = computed(() => {
    const entries = this.sortedEntries();
    this.formVersion();

    return entries
      .map((entry) => {
        if (!entry.product?.code || !entry.positionGuid) return null;

        const control = this.form.get(
          this.createControlName(entry.product.code, entry.positionGuid),
        ) as FormGroup | null;

        return control ? { entry, control } : null;
      })
      .filter((vm): vm is { entry: CustomOrderEntry; control: FormGroup } => vm !== null);
  });

  constructor() {
    this.trackByEntry = this.trackByEntry.bind(this);

    effect(
      () => {
        const items = this.entries();
        this.patchForm(items);
        this.formVersion.update((v) => v + 1);
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      const loading = this.isInternalLoading();
      if (loading) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    });
  }

  ngOnDestroy(): void {
    this.deviceService.isTabletMdDown$.pipe(take(1), filter(Boolean)).subscribe(() => {
      this.scrollToService.scrollToTop();
    });
  }

  trackByEntry(_index: number, vm: { entry: CustomOrderEntry; control: FormGroup }): string {
    return this.createControlName(vm.entry.product?.code, vm.entry.positionGuid);
  }

  private createControlName(code: string | number | undefined, order: string | number | undefined): string {
    return `${code}_${order}`;
  }

  private patchForm(items: CustomOrderEntry[]): void {
    const newKeys = new Set(
      items
        .filter((i) => i.product?.code && i.positionGuid)
        .map((i) => this.createControlName(i.product.code, i.positionGuid)),
    );

    Object.keys(this.form.controls)
      .filter((key) => !newKeys.has(key))
      .forEach((key) => this.form.removeControl(key));

    items.forEach((item) => {
      if (!item.product?.code || !item.positionGuid) return;

      const key = this.createControlName(item.product.code, item.positionGuid);

      if (this.form.contains(key)) {
        const group = this.form.get(key) as FormGroup;
        const quantityControl = group.get('quantity');
        if (!quantityControl?.dirty && quantityControl?.value !== item.quantity) {
          quantityControl?.setValue(item.quantity, { emitEvent: false });
        }
      } else {
        const group = new FormGroup({
          positionGuid: new FormControl(item.positionGuid),
          quantity: new FormControl(item.quantity, { updateOn: 'blur' }),
        });

        group.valueChanges
          .pipe(
            distinctUntilChanged(
              (prev, next) => prev?.positionGuid === next?.positionGuid && prev?.quantity === next?.quantity,
            ),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe((value) => {
            if (value?.positionGuid && item.updateable) {
              this.activeCartService.debouncedUpdateEntry(value.positionGuid, value.quantity || 0);
            }
          });

        if (!item.updateable) {
          group.disable();
        }

        this.form.setControl(key, group);
      }
    });
  }
}
